<template>
  <div class="biography-cover-media">
    <img
      :key="source.url"
      :src="source.url"
      :data-source="source.url"
      :alt="imageAlt"
      :loading="loading"
      :style="imageStyle"
      decoding="async"
      @error="advanceSource"
    />
  </div>
</template>

<script>
import defaultCover from '@/assets/images/biographies/default-cover.webp'

const avatarBase = 'https://self-biography-system.s3.us-east-2.amazonaws.com/'

export default {
  name: 'BiographyCover',
  props: {
    profile: { type: Object, required: true },
    preferAvatar: { type: Boolean, default: false },
    loading: {
      type: String,
      default: 'lazy',
      validator: value => ['lazy', 'eager'].includes(value),
    },
  },
  data: () => ({
    attempt: 0,
    size: { width: 0, height: 0 },
    resizeObserver: null,
  }),
  computed: {
    sources() {
      const cover = this.profile.cover_url && {
        url: this.profile.cover_url,
        rotation: Number(this.profile.cover_rotation) || 0,
        fallback: false,
      }
      const avatar = this.profile.avatar && {
        url: /^https?:\/\//i.test(this.profile.avatar) ? this.profile.avatar : `${avatarBase}${this.profile.avatar}`,
        rotation: 0,
        fallback: false,
      }

      return [
        ...(this.preferAvatar ? [avatar, cover] : [cover, avatar]),
        { url: defaultCover, rotation: 0, fallback: true },
      ].filter(Boolean)
    },
    sourceIdentity() {
      return JSON.stringify([this.profile.id, this.sources])
    },
    source() {
      return this.sources[Math.min(this.attempt, this.sources.length - 1)]
    },
    imageAlt() {
      const name = this.profile.name || this.profile.username || 'this biography'

      return `${this.source.fallback ? 'Illustrated cover' : 'Cover image'} for ${name}`
    },
    imageStyle() {
      const rotation = ((Math.round(this.source.rotation) % 4) + 4) % 4
      const quarterTurn = rotation % 2 === 1 && this.size.width && this.size.height

      return {
        width: quarterTurn ? `${this.size.height}px` : '100%',
        height: quarterTurn ? `${this.size.width}px` : '100%',
        transform: `translate(-50%, -50%) rotate(${rotation * 90}deg)`,
      }
    },
  },
  watch: {
    sourceIdentity() {
      this.attempt = 0
    },
    source: {
      immediate: true,
      handler(value) {
        this.$emit('source-change', { fallback: value.fallback })
      },
    },
  },
  mounted() {
    this.measure()
    if (typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver(this.measure)
      this.resizeObserver.observe(this.$el)
    } else {
      window.addEventListener('resize', this.measure)
    }
  },
  beforeDestroy() {
    if (this.resizeObserver) this.resizeObserver.disconnect()
    window.removeEventListener('resize', this.measure)
  },
  methods: {
    measure() {
      const { width, height } = this.$el.getBoundingClientRect()
      this.size = { width, height }
    },
    advanceSource(event) {
      if (event.target.dataset.source !== this.source.url) return
      if (this.attempt < this.sources.length - 1) this.attempt += 1
    },
  },
}
</script>

<style scoped>
.biography-cover-media { position: absolute; inset: 0; overflow: hidden; }
.biography-cover-media img { position: absolute; top: 50%; left: 50%; display: block; object-fit: cover; }
</style>
